// Pure formula evaluation for the Efficiency KPI tile — no React dependencies.
//
// Rows are combined strictly in aggregation-then-division order:
// each row's attribute sum comes pre-aggregated from the backend (numericSums),
// rows are combined left-to-right via their operator, then numerator / denominator * 100.

const combineRows = (rows, numericSums) => {
  let acc = null;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const sum = numericSums?.[row.attribute];
    if (sum === undefined || sum === null || Number.isNaN(sum)) return null;
    if (i === 0) {
      acc = sum;
    } else if (row.operator === '-') {
      acc -= sum;
    } else {
      acc += sum;
    }
  }
  return acc;
};

/**
 * @param {{ numerator: Array<{attribute: string, operator: '+'|'-'|null}>, denominator: Array<{attribute: string, operator: '+'|'-'|null}>, numericSums: Record<string, number|null> }} params
 * @returns {{ value: number, error: null } | { value: null, error: 'MISSING_DATA' | 'DENOMINATOR_ZERO' }}
 */
export const computeFormulaValue = ({ numerator, denominator, numericSums }) => {
  const numeratorSum = combineRows(numerator || [], numericSums);
  const denominatorSum = combineRows(denominator || [], numericSums);

  if (numeratorSum === null || denominatorSum === null) {
    return { value: null, error: 'MISSING_DATA' };
  }
  if (denominatorSum === 0) {
    return { value: null, error: 'DENOMINATOR_ZERO' };
  }
  return { value: (numeratorSum / denominatorSum) * 100, error: null };
};

/** Deduplicated list of attributes referenced by either side of the formula, for the object-table request. */
export const collectFormulaAttributes = (numerator, denominator) => {
  const attrs = new Set();
  (numerator || []).forEach((r) => r.attribute && attrs.add(r.attribute));
  (denominator || []).forEach((r) => r.attribute && attrs.add(r.attribute));
  return Array.from(attrs);
};

/** 3-zone traffic light color: value < lower → red, lower <= value < upper → yellow, value >= upper → green. */
export const resolveAmpelColor = (value, { lower, upper, colorRed, colorYellow, colorGreen }) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  if (lower === null || lower === undefined || upper === null || upper === undefined) return null;
  if (value < lower) return colorRed;
  if (value < upper) return colorYellow;
  return colorGreen;
};
